/**
 * CZAS ŚCIENNY Z SPS NA EKRANIE — formatowanie TEKSTOWE, bez `Date`.
 *
 * ⚠️ ŻADNEGO `new Date()` W TYM PLIKU I TO JEST CAŁA JEGO TREŚĆ. Znacznik wydania przychodzi
 * z API jako `2026-08-05 14:34:34` — odczyt zegara maszyny, nie chwila. Przepuszczenie go przez
 * `new Date(...).toLocaleString('pl-PL')` daje wynik zależny od strefy PRZEGLĄDARKI, czyli
 * dokładnie ten błąd, który naprawiliśmy w bazie: wydanie o 14:34 pokazywało się jako 16:34.
 *
 * Wymaganie klienta brzmi: „pobrał o 15:20, w raporcie ma być 15:20; ważne, żeby minuty się nie
 * rozjebały". Jedyny sposób, żeby nie rozjechały się NIGDY, to nie liczyć — tylko przestawiać
 * znaki. Stąd te funkcje wyglądają na prymitywne i takie mają być.
 *
 * ⚠️ TO NIE DOTYCZY NASZYCH DAT. `createdAt`, `lastSeenAt`, `enrolledAt` i reszta metadanych to
 * prawdziwe chwile z `timestamptz` — te formatuje się normalnie, przez `toLocaleString`, i mają
 * być pokazywane w strefie patrzącego. Dwa rodzaje dat, dwa sposoby, świadomie.
 */

/** `2026-08-05 14:34:34` albo `2026-08-05T14:34:34` — z sekundami lub bez. */
const WZORZEC = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

/** `05.08.2026, 14:34` — układ jak w `pl-PL`, ale złożony ze znaków, nie policzony. */
export function czasSps(napis: string | null | undefined): string {
  if (napis === null || napis === undefined) return '—';
  const m = WZORZEC.exec(napis);
  if (m === null) return napis;
  const [, rok, mies, dzien, godz, min] = m;
  return `${dzien}.${mies}.${rok}, ${godz}:${min}`;
}

/** Sama data — `5 sierpnia 2026`. Do zdania o zakresie godzina jest szumem. */
const MIESIACE = [
  'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
  'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia',
];

export function dataSps(napis: string | null | undefined): string {
  if (napis === null || napis === undefined) return '—';
  const m = WZORZEC.exec(napis);
  if (m === null) return napis;
  const [, rok, mies, dzien] = m;
  return `${Number(dzien)} ${MIESIACE[Number(mies) - 1] ?? mies} ${rok}`;
}

/**
 * GRANICA ZAKRESU dla filtrów — czas ścienny, nie chwila.
 *
 * ⚠️ Wcześniej front wysyłał `startOfDay(now).toISOString()`, czyli dla Polski
 * `…T22:00:00.000Z` — początek doby przesunięty o dwie godziny wstecz. Wydanie o 22:30 wpadało
 * przez to do sąsiedniego dnia w raporcie dziennym. Serwer takiej postaci już nie przyjmie
 * (odrzuca napisy ze strefą), więc ten błąd nie może wrócić po cichu.
 */
export function granicaDnia(d: Date, koniec = false): string {
  const dwa = (n: number) => String(n).padStart(2, '0');
  const data = `${d.getFullYear()}-${dwa(d.getMonth() + 1)}-${dwa(d.getDate())}`;
  return `${data} ${koniec ? '23:59:59' : '00:00:00'}`;
}

/**
 * Granica z pola typu `date` w formularzu (`YYYY-MM-DD`) — bez przechodzenia przez `Date`.
 *
 * ⚠️ GODZINA JEST NIEOBOWIĄZKOWA i domyślnie oznacza CAŁĄ DOBĘ (`00:00:00`–`23:59:59`). Podana
 * zawęża zakres do konkretnych godzin — powstało z pytania, które realnie pada u klienta:
 * „co pobierał Andrzej między 10 a 14 wczoraj".
 *
 * ⚠️ KONIEC ZAKRESU DOMYKA CAŁĄ MINUTĘ (`:59`), a nie ucina ją na sekundzie zerowej. „Do 14:00"
 * czyta się jako „do czternastej", więc wydanie o 14:00:30 musi się w tym zmieścić — inaczej
 * raport gubi pojedyncze wiersze dokładnie na granicy i wygląda to na usterkę danych.
 */
export function granicaZPola(wartosc: string, koniec = false, godzina?: string): string {
  if (godzina !== undefined && godzina !== '') return `${wartosc} ${godzina}:${koniec ? '59' : '00'}`;
  return `${wartosc} ${koniec ? '23:59:59' : '00:00:00'}`;
}
